import { Box, Stack, TextField } from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { SettingsDangerZoneRow } from "~/components/settings/DangerZoneWrapper";
import { OverviewFieldRow } from "~/features/settings/components/OverviewFieldRow";
import {
  settingsOutlinedTextFieldSx,
  settingsSiteSelectMaxWidthPx
} from "~/features/settings/settings.styles";
import { setDropdownWorkstationId } from "~/features/workstationConfig/workstationConfig.slice";
import { useSetThisWorkstation } from "~/hooks/useSetThisWorkstation";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectDropdownWorkstation } from "~/redux/selectors/workstationConfigSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useDeleteWorkstationMutation } from "~/redux/warehouse/workstation.hooks";

const DELETE_TOKEN = "DELETE";

export function WorkstationDangerZone() {
  const { t } = useTranslation();
  const { errorToast, successToast } = useToast();
  const [deleteInput, setDeleteInput] = useState("");

  const dispatch = useAppDispatch();
  const selectedWorkstation = useAppSelector(selectDropdownWorkstation);
  const siteWorkstation = useAppSelector(selectThisWorkstation);

  const isSelectedSiteWorkstation =
    !!siteWorkstation &&
    !!selectedWorkstation &&
    selectedWorkstation.id === siteWorkstation.id;

  const [deleteWorkstation, { isLoading: isDeleting }] =
    useDeleteWorkstationMutation();
  const { setThisWorkstation, isLoading: isSettingSite } =
    useSetThisWorkstation();

  const isDeleteInProgress = isDeleting || isSettingSite;

  const canSubmitDelete =
    deleteInput.trim() === DELETE_TOKEN &&
    !!selectedWorkstation &&
    !isDeleteInProgress;

  useEffect(() => {
    setDeleteInput("");
  }, [selectedWorkstation?.id]);

  const handleConfirmDelete = async () => {
    if (!canSubmitDelete || !selectedWorkstation) return;
    try {
      if (isSelectedSiteWorkstation) {
        await setThisWorkstation(null, true);
      }
      await deleteWorkstation({
        workstationId: selectedWorkstation.id
      }).unwrap();
      dispatch(setDropdownWorkstationId(""));
      successToast(t("workstation deleted toast"));
      setDeleteInput("");
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    }
  };

  if (!selectedWorkstation) {
    return null;
  }

  return (
    <Stack id="ws-danger-zone-root" spacing={2}>
      <OverviewFieldRow
        id="ws-danger-zone-target-row"
        labelId="danger-zone-workstation-label"
        label={t("danger zone target workstation")}
      >
        <Box
          id="ws-danger-zone-target-display-wrap"
          sx={{
            width: "100%",
            minWidth: 0,
            maxWidth: {
              xs: "100%",
              sm: settingsSiteSelectMaxWidthPx
            }
          }}
        >
          <TextField
            id="danger-zone-workstation-display"
            size="small"
            variant="outlined"
            fullWidth
            disabled
            value={selectedWorkstation.deviceId}
            slotProps={{
              htmlInput: {
                "aria-labelledby": "danger-zone-workstation-label",
                readOnly: true
              }
            }}
            sx={(theme) => settingsOutlinedTextFieldSx(theme)}
          />
        </Box>
      </OverviewFieldRow>
      <SettingsDangerZoneRow
        title={t("delete workstation")}
        hint={t("workstation delete caution")}
        confirmationPhrase={DELETE_TOKEN}
        confirmationValue={deleteInput}
        onConfirmationChange={setDeleteInput}
        onAction={() => void handleConfirmDelete()}
        actionDisabled={!canSubmitDelete}
      />
    </Stack>
  );
}
